import { DefaultThemeOptions, FormsFeature } from "@ngt/forms";
import * as dtos from '@/api/dtos';
import logo from '@/assets/img/logo-small.png';
import { alpha, buttonClasses, colors, getContrastRatio, responsiveFontSizes, ThemeOptions } from "@mui/material";
import { grey } from "@mui/material/colors";

import GTPressuraBoldEot from './assets/fonts/GTPressura-Bold.eot';
import GTPressuraBoldSvg from './assets/fonts/GTPressura-Bold.svg';
import GTPressuraBoldTtf from './assets/fonts/GTPressura-Bold.ttf';
import GTPressuraBoldWoff from './assets/fonts/GTPressura-Bold.woff';
import GTPressuraBoldWoff2 from './assets/fonts/GTPressura-Bold.woff2';

import GTPressuraLightEot from './assets/fonts/GTPressura-Light.eot';
import GTPressuraLightSvg from './assets/fonts/GTPressura-Light.svg';
import GTPressuraLightTtf from './assets/fonts/GTPressura-Light.ttf';
import GTPressuraLightWoff from './assets/fonts/GTPressura-Light.woff';
import GTPressuraLightWoff2 from './assets/fonts/GTPressura-Light.woff2';

import GTPressuraBoldItalicEot from './assets/fonts/GTPressura-BoldItalic.eot';
import GTPressuraBoldItalicSvg from './assets/fonts/GTPressura-BoldItalic.svg';
import GTPressuraBoldItalicTtf from './assets/fonts/GTPressura-BoldItalic.ttf';
import GTPressuraBoldItalicWoff from './assets/fonts/GTPressura-BoldItalic.woff';
import GTPressuraBoldItalicWoff2 from './assets/fonts/GTPressura-BoldItalic.woff2';

import GTPressuraRegularItalicEot from './assets/fonts/GTPressuraRegular-Italic.eot';
import GTPressuraRegularItalicSvg from './assets/fonts/GTPressuraRegular-Italic.svg';
import GTPressuraRegularItalicTtf from './assets/fonts/GTPressuraRegular-Italic.ttf';
import GTPressuraRegularItalicWoff from './assets/fonts/GTPressuraRegular-Italic.woff';
import GTPressuraRegularItalicWoff2 from './assets/fonts/GTPressuraRegular-Italic.woff2';

import GTPressuraEot from './assets/fonts/GTPressura.eot';
import GTPressuraSvg from './assets/fonts/GTPressura.svg';
import GTPressuraTtf from './assets/fonts/GTPressura.ttf';
import GTPressuraWoff from './assets/fonts/GTPressura.woff';
import GTPressuraWoff2 from './assets/fonts/GTPressura.woff2';

import GTPressuraLightItalicEot from './assets/fonts/GTPressura-LightItalic.eot';
import GTPressuraLightItalicTtf from './assets/fonts/GTPressura-LightItalic.ttf';
import GTPressuraLightItalicWoff from './assets/fonts/GTPressura-LightItalic.woff';
import GTPressuraLightItalicWoff2 from './assets/fonts/GTPressura-LightItalic.woff2';


const primaryColorLight = '#502d77';
const secondaryColorLight = '#EC008C';
const tertiaryColorLight = '#76c6d7';
const primaryColorDark = '#502d77';
const secondaryColorMoreDark = '#EC008C';

// Store initial values before they can be cleared
const INITIAL_AUTH = (window as any).AUTH;
const INITIAL_FORM_METADATA = (window as any).FORM_METADATA;
const INITIAL_METADATA_TYPES = (window as any).METADATA_TYPES;

const formsFeature = new FormsFeature({
    dtos,
    baseRoute: '/conference',
    extensions: [

    ],
    organisationOptions: {
        organisationName: 'Breast Cancer Trials',
        organisationLogo: logo
    },
    client: {
        baseUrl: import.meta.env.VITE_API_URL,
        basePath: '/api',
        client: undefined
    },
    themeOptions: {
        beforeThemeCreated: (themeOptions, chosenTheme) => {
            const newOptions: ThemeOptions = {
                ...themeOptions,
                palette: {
                    ...themeOptions.palette,
                    mode: 'light',
                    primary: {
                        main: primaryColorLight
                    },
                    secondary: {
                        main: secondaryColorLight 
                    },
                    tertiary: {
                        main: tertiaryColorLight,
                        light: alpha(tertiaryColorLight, 0.5),
                        dark: alpha(tertiaryColorLight, 0.9),
                        contrastText: '#fff' //getContrastRatio(tertiaryColorLight, '#fff') > 4.5 ? '#fff' : '#111',
                    },
                    background: {
                        //default: "#fff"
                        default: grey[200]
                    }
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: `
                            @font-face {
                                font-family: 'GTPressura';
                                font-style: normal;
                                font-weight: normal;
                                font-display: auto;
                                src: url('${GTPressuraEot}');
                                src: url('${GTPressuraEot}?#iefix') format('embedded-opentype'),
                                    url('${GTPressuraWoff2}') format('woff2'),
                                    url('${GTPressuraWoff}') format('woff'),
                                    url('${GTPressuraTtf}') format('truetype'),
                                    url('${GTPressuraSvg}#GTPressura') format('svg');
                            }
                            @font-face {
                                font-family: 'GTPressura';
                                font-style: normal;
                                font-weight: bold;
                                font-display: auto;
                                src: url('${GTPressuraBoldEot}');
                                src: url('${GTPressuraBoldEot}?#iefix') format('embedded-opentype'),
                                    url('${GTPressuraBoldWoff2}') format('woff2'),
                                    url('${GTPressuraBoldWoff}') format('woff'),
                                    url('${GTPressuraBoldTtf}') format('truetype'),
                                    url('${GTPressuraBoldSvg}#GTPressura') format('svg');
                            }
                            @font-face {
                                font-family: 'GTPressura';
                                font-style: italic;
                                font-weight: normal;
                                font-display: auto;
                                src: url('${GTPressuraRegularItalicEot}');
                                src: url('${GTPressuraRegularItalicEot}?#iefix') format('embedded-opentype'),
                                    url('${GTPressuraRegularItalicWoff2}') format('woff2'),
                                    url('${GTPressuraRegularItalicWoff}') format('woff'),
                                    url('${GTPressuraRegularItalicTtf}') format('truetype'),
                                    url('${GTPressuraRegularItalicSvg}#GTPressura') format('svg');
                            }
                            @font-face {
                                font-family: 'GTPressura';
                                font-style: normal;
                                font-weight: 100;
                                font-display: auto;
                                src: url('${GTPressuraLightEot}');
                                src: url('${GTPressuraLightEot}?#iefix') format('embedded-opentype'),
                                    url('${GTPressuraLightWoff2}') format('woff2'),
                                    url('${GTPressuraLightWoff}') format('woff'),
                                    url('${GTPressuraLightTtf}') format('truetype'),
                                    url('${GTPressuraLightSvg}#GTPressura') format('svg');
                            }
                            @font-face {
                                font-family: 'GTPressura';
                                font-style: italic;
                                font-weight: bold;
                                font-display: auto;
                                src: url('${GTPressuraBoldItalicEot}');
                                src: url('${GTPressuraBoldItalicEot}?#iefix') format('embedded-opentype'),
                                    url('${GTPressuraBoldItalicWoff2}') format('woff2'),
                                    url('${GTPressuraBoldItalicWoff}') format('woff'),
                                    url('${GTPressuraBoldItalicTtf}') format('truetype'),
                                    url('${GTPressuraBoldItalicSvg}#GTPressura') format('svg');
                            }
                            @font-face {
                                font-family: 'GTPressura';
                                font-style: italic;
                                font-weight: 100;
                                font-display: auto;
                                src: url('${GTPressuraLightItalicEot}');
                                src: url('${GTPressuraLightItalicEot}?#iefix') format('embedded-opentype'),
                                    url('${GTPressuraLightItalicWoff2}') format('woff2'),
                                    url('${GTPressuraLightItalicWoff}') format('woff'),
                                    url('${GTPressuraLightItalicTtf}') format('truetype');
                            }
                        `
                    },
                    MuiButton: {
                        defaultProps: {
                            variant: 'contained'
                        },
                        styleOverrides: {
                            root: {
                                fontWeight: 'bold',
                                fontFamily: 'GTPressura',
                                padding: 2 * 5,
                                minWidth: 120,
                                textTransform: 'none',
                                fontSize: '1.125rem',

                                '&.MuiButton-containedTertiary': {
                                    //color: '#fff !important'
                                    '&:hover': {
                                        backgroundColor: secondaryColorLight
                                    }
                                }
                            }
                        }
                    },
                    MuiSelect: {
                        styleOverrides: {
                            icon: {
                                position: 'relative'
                            }
                        }
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            // root: {
                            //     borderRadius: 50
                            // }
                        }
                    },
                    MuiToggleButtonGroup: {
                        // defaultProps: {
                        //     color: "primary"
                        // },
                        styleOverrides: {
                            root: {
                                "& .MuiButtonBase-root.MuiToggleButton-root.Mui-selected": {backgroundColor: "#502d77", color: "white"}
                            }
                        }
                    },
                    MuiFormControlLabel:{
                        styleOverrides: {
                            root: {
                                "& #full": {fontSize: "3rem"},
                            }
                        }
                    },
                    MuiSvgIcon: {
                        styleOverrides:{
                            root:{
                                "&.MuiSvgIcon-fontSizeMedium.MuiStepIcon-root": {color: "#502d77"},
                                "&.MuiSvgIcon-fontSizeMedium.MuiStepIcon-root.Mui-active": {color: "#EC008C"}
                            }
                        }
                    },
                    MuiStepLabel:{
                        styleOverrides: {
                            root: {
                                "& .MuiStepLabel-label": {color: "#502d77"},
                                "& .MuiStepLabel-label.Mui-completed": {color: "#502d77"},
                                "& .MuiStepLabel-label.Mui-active": {fontSize: "1.2rem", color: "#EC008C"}
                            }
                        }
                    }
                }
            };
            
            return newOptions;
        },
        afterThemeCreated: (theme, chosenTheme) => {
            return responsiveFontSizes(theme as any) as any;
        },
        themeOptions: DefaultThemeOptions
    },
    initialAuthentication: INITIAL_AUTH,
    initialFormMetadata: INITIAL_FORM_METADATA, 
    initialMetadataTypes: INITIAL_METADATA_TYPES,
    disableAuthentication: false
});

formsFeature.serviceStackClient.exceptionFilter = (res: Response, _: any) => {
    if (res.status === 401) {
        global.location.href = `/auth/openiddict?redirect=${encodeURIComponent(global.location.pathname + global.location.search)}`;
    }
};

export default formsFeature;